// File: src/pages/reports/DeliveredOrderTimeline.js
import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Row, Col, Form } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import moment from "moment-timezone";
import "bootstrap/dist/css/bootstrap.min.css";
import DynamicBreadcrumb from "../header/breadcrumb";
import reportApi from "../../services/reportapi";
import Loader from "../loader/loader";
import branchApi from "../../services/branchapi";

const DeliveredOrderTimeline = () => {
    // console.log('Triggered');
    const TZ = "Asia/Kolkata";
    const last7From = moment.tz(TZ).startOf("day").subtract(6, "days").format("YYYY-MM-DD");
    const last7To = moment.tz(TZ).endOf("day").format("YYYY-MM-DD");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortModel, setSortModel] = useState([{ field: "postedDate", sort: "desc" }]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
    const [loaderVisible, setLoaderVisible] = useState(false);
    const [branches, setBranches] = useState([]);

    const [filters, setFilters] = useState({
        fromDate: "",
        toDate: "",
        branchId: "",
        status: "6",
    });

    const updateFilters = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    const fetchRecord = useCallback(async () => {
        setLoaderVisible(true);
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const payload = {
                limit: pageSize,
                page,
                sort: sortModel,
                // free-text quick filter
                search: filterModel?.quickFilterValues?.[0] ?? "",
                fromDate: filters.fromDate || null,
                toDate: filters.toDate || null,
                branchId: filters.branchId || null,
                status: filters.status || 6,
            };

            const data = await reportApi.getDeliveredOrderTimeline(payload);

            const formattedRows = (data?.data?.result || []).map((o, idx) => {
                const placed = o.postedDate ? moment(o.postedDate) : null;
                const confirmed = o.confirmedAt ? moment(o.confirmedAt) : null;
                const packed = o.packedAt ? moment(o.packedAt) : null;
                const shipped = o.shippedAt ? moment(o.shippedAt) : null;
                const delivered = o.deliveredAt ? moment(o.deliveredAt) : null;

                return {
                    id: o.id ?? `${o.orderShowId}-${idx}`, // ensure a stable id
                    sno: idx + 1 + page * pageSize,
                    showId: o.orderShowId,
                    customerName: o.customerName || "",
                    customerMobile: o.customerMobile || "",
                    branchName: o.branchName || o.branchDetails?.branchName || "",
                    postedDate: o.postedDate || null,
                    postedDateFmt: placed ? placed.format("YYYY-MM-DD HH:mm") : "",
                    confirmedAt: o.confirmedAt || null,
                    confirmedAtFmt: confirmed ? confirmed.format("YYYY-MM-DD HH:mm") : "",
                    packedAt: o.packedAt || null,
                    packedAtFmt: packed ? packed.format("YYYY-MM-DD HH:mm") : "",
                    shippedAt: o.shippedAt || null,
                    shippedAtFmt: shipped ? shipped.format("YYYY-MM-DD HH:mm") : "",
                    deliveredAt: o.deliveredAt || null,
                    deliveredAtFmt: delivered ? delivered.format("YYYY-MM-DD HH:mm") : "",

                    t_confirmed_m: placed && confirmed ? confirmed.diff(placed, "minutes") : null,
                    t_packed_m: confirmed && packed ? packed.diff(confirmed, "minutes") : null,
                    t_shipped_m: packed && shipped ? shipped.diff(packed, "minutes") : null,
                    t_delivered_m: shipped && delivered ? delivered.diff(shipped, "minutes") : null,
                };
            });

            setRows(formattedRows);
            setTotalRecords(data?.data?.totalRecords || 0);
        } catch (err) {
            console.error("Failed to fetch delivered order timeline:", err);
        } finally {
            setLoading(false);
            setLoaderVisible(false);
        }
    }, [paginationModel, sortModel, filterModel, filters]);

    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            fromDate: last7From,
            toDate: moment.tz(TZ).format("YYYY-MM-DD"), // input[type=date] expects date only
        }));
    }, []);
    useEffect(() => {
        fetchRecord();
    }, [fetchRecord]);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchData = await branchApi.getAllBranches({
                    limit: 100,
                    page: 0,
                    sortModel: [{ field: "branchName", sort: "asc" }],
                });
                setBranches(branchData.data.result || []);
            } catch (e) {
                console.error("Error fetching branches:", e);
            }
        };
        fetchBranches();
    }, []);

    const exportData = async () => {
        try {
            setLoaderVisible(true);
            const payload = {
                limit: totalRecords || 10000,
                page: 0,
                sort: sortModel,
                search: filterModel?.quickFilterValues?.[0] ?? "",
                fromDate: filters.fromDate || null,
                toDate: filters.toDate || null,
                branchId: filters.branchId || null,
                status: filters.status || 6,
            };
            const data = await reportApi.getDeliveredOrderTimeline(payload);
            const all = data?.data?.result || [];
            const csvRows = [
                [
                    "SNO",
                    "Order ID",
                    "Customer",
                    "Mobile",
                    "Branch",
                    "Placed At",
                    "Confirmed At",
                    "Packed At",
                    "Shipped At",
                    "Delivered At",
                    "Min to Confirm",
                    "Min to Pack",
                    "Min to Ship",
                    "Min to Deliver",
                ],
            ];
            all.forEach((o, i) => {
                const placed = o.postedDate ? moment(o.postedDate) : null;
                const confirmed = o.confirmedAt ? moment(o.confirmedAt) : null;
                const packed = o.packedAt ? moment(o.packedAt) : null;
                const shipped = o.shippedAt ? moment(o.shippedAt) : null;
                const delivered = o.deliveredAt ? moment(o.deliveredAt) : null;
                csvRows.push([
                    i + 1,
                    o.orderShowId,
                    o.customerName || "",
                    o.customerMobile || "",
                    o.branchName || "",
                    placed ? placed.format("YYYY-MM-DD HH:mm") : "",
                    confirmed ? confirmed.format("YYYY-MM-DD HH:mm") : "",
                    packed ? packed.format("YYYY-MM-DD HH:mm") : "",
                    shipped ? shipped.format("YYYY-MM-DD HH:mm") : "",
                    delivered ? delivered.format("YYYY-MM-DD HH:mm") : "",
                    placed && confirmed ? confirmed.diff(placed, "minutes") : "",
                    confirmed && packed ? packed.diff(confirmed, "minutes") : "",
                    packed && shipped ? shipped.diff(packed, "minutes") : "",
                    shipped && delivered ? delivered.diff(shipped, "minutes") : "",
                ]);
            });
            const csvContent = csvRows.map((e) => e.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", "delivered_order_timeline.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Export failed:", err);
        } finally {
            setLoaderVisible(false);
        }
    };

    const smallMuted = { fontSize: 12, opacity: 0.7 };
    const columns = [
        { field: "sno", headerName: "S NO", width: 80 },
        { field: "postedDateFmt", headerName: "Placed At", flex: 1 },
        { field: "showId", headerName: "Order ID", flex: 1 },
        { field: "branchName", headerName: "Branch", flex: 1 },
        { field: "customerName", headerName: "Customer", flex: 1 },
        { field: "customerMobile", headerName: "Mobile", flex: 1 },

        {
            field: "confirmedAtFmt",
            headerName: "Confirmed At",
            flex: 1,
            renderCell: ({ row }) => (
                <div>
                    <div>{row.confirmedAtFmt || "-"}</div>
                    {row.t_confirmed_m != null && (
                        <div style={smallMuted}>min → Confirm: {row.t_confirmed_m}m</div>
                    )}
                </div>
            ),
        },
        {
            field: "packedAtFmt",
            headerName: "Packed At",
            flex: 1,
            renderCell: ({ row }) => (
                <div>
                    <div>{row.packedAtFmt || "-"}</div>
                    {row.t_packed_m != null && (
                        <div style={smallMuted}>min → Pack: {row.t_packed_m}m</div>
                    )}
                </div>
            ),
        },
        {
            field: "shippedAtFmt",
            headerName: "Shipped At",
            flex: 1,
            renderCell: ({ row }) => (
                <div>
                    <div>{row.shippedAtFmt || "-"}</div>
                    {row.t_shipped_m != null && (
                        <div style={smallMuted}>min → Ship: {row.t_shipped_m}m</div>
                    )}
                </div>
            ),
        },
        {
            field: "deliveredAtFmt",
            headerName: "Delivered At",
            flex: 1,
            renderCell: ({ row }) => (
                <div>
                    <div>{row.deliveredAtFmt || "-"}</div>
                    {row.t_delivered_m != null && (
                        <div style={smallMuted}>min → Deliver: {row.t_delivered_m}m</div>
                    )}
                </div>
            ),
        },
    ];

    const pathArray = [{ name: "Delivered Order Timeline", url: null }];

    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Delivered Orders – Timeline</h5>
            </div>

            {/* Filters */}
            <Form className="mb-3">
                <Row>
                    <Col md={2}>
                        <Form.Label>Branch</Form.Label>
                        <Form.Select
                            value={filters.branchId}
                            onChange={(e) => updateFilters("branchId", e.target.value)}
                        >
                            <option value="">All Branches</option>
                            {branches.map((branch) => (
                                <option key={branch.branchId} value={branch.branchId}>
                                    {branch.branchName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    {/* NEW: Status filter */}
                    <Col md={2}>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                            value={filters.status}
                            onChange={(e) => updateFilters("status", e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="1">Pending</option>
                            <option value="2">Confirmed</option>
                            <option value="4">Packed</option>
                            <option value="5">Shipped</option>
                            <option value="6">Delivered</option>
                            <option value="3">Admin Cancel</option>
                            <option value="7">Customer Cancel</option>
                            <option value="8">Customer Refund</option>
                            <option value="10">Refund Approved</option>
                        </Form.Select>
                    </Col>

                    <Col md={2}>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.fromDate}
                            onChange={(e) => updateFilters("fromDate", e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.toDate}
                            onChange={(e) => updateFilters("toDate", e.target.value)}
                        />
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                        <Button variant="primary" onClick={() => fetchRecord()}>
                            Filter
                        </Button>
                        <Button variant="success" className="ms-2" onClick={exportData}>
                            Export
                        </Button>
                    </Col>
                </Row>
            </Form>

            {loaderVisible && <Loader />}

            <Box>
                <DataGrid
                    initialState={{
                        pagination: { paginationModel },
                        sorting: { sortModel },
                    }}
                    disableRowSelectionOnClick
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    rowCount={totalRecords}
                    pageSizeOptions={[5, 10, 15, 20]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={setSortModel}
                    onFilterModelChange={setFilterModel}
                    loading={loading}
                    checkboxSelection
                    onRowSelectionModelChange={(row) => setRowSelectionModel(row)}
                    rowSelectionModel={rowSelectionModel}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    sx={{ [`& .${gridClasses.cell}`]: { py: 1 } }}
                />
            </Box>
        </Container>
    );
};

export default DeliveredOrderTimeline;
